import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { Clock, History, Loader2, RefreshCw, Trash2, Users } from "lucide-react";

import TikTokIcon from "@/assets/tiktok";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  deleteTikTokCreatorSearch,
  getTikTokCreatorSearch,
  getTikTokCreatorSearchCatalog,
  listTikTokCreatorSearches,
  loadMoreTikTokCreators,
  searchTikTokCreators,
  type TikTokCreatorSearch as SearchResult,
  type TikTokCreatorSearchFilters,
  type TikTokCreatorSearchHistoryItem,
  type TikTokMarketplaceCreator,
} from "@/services/tiktokShop.service";

import { CreatorDetailSheet } from "./CreatorDetailSheet";
import { CreatorResultsTable } from "./CreatorResultsTable";
import { CreatorSearchFilters, SortSelect } from "./CreatorSearchFilters";
import {
  blockFromError,
  compactFilters,
  errorMessage,
  EXAMPLE_FILTERS,
  formatCountdown,
  retryAfterSeconds,
  type CreatorSearchBlock,
} from "./creatorSearchFormat";

const KEYS = {
  catalog: ["tiktok-creator-search", "catalog"] as const,
  history: ["tiktok-creator-search", "history"] as const,
};

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

/** Seconds left until `until`, ticking once a second; null when not limited. */
function useCountdown(until: number | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [until]);
  if (until === null) return null;
  const left = Math.ceil((until - now) / 1000);
  return left > 0 ? left : null;
}

/**
 * Social Search › TikTok creators: TikTok Shop's Creator Marketplace, so the
 * brand finds creators open to collaborations in its market, including ones
 * who have never sold its products. No credits: TikTok's API is free, so
 * searches are rate limited instead.
 */
export function TikTokCreatorSearch({ header }: { header: ReactNode }) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<TikTokCreatorSearchFilters>({});
  const [result, setResult] = useState<SearchResult | null>(null);
  const [fromHistory, setFromHistory] = useState(false);
  const [selected, setSelected] = useState<TikTokMarketplaceCreator | null>(null);
  const [limitedUntil, setLimitedUntil] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const secondsLeft = useCountdown(limitedUntil);

  const catalogQuery = useQuery({
    queryKey: KEYS.catalog,
    queryFn: getTikTokCreatorSearchCatalog,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  const block: CreatorSearchBlock | null = catalogQuery.isError ? blockFromError(catalogQuery.error) : null;

  const historyQuery = useQuery({
    queryKey: KEYS.history,
    queryFn: listTikTokCreatorSearches,
    enabled: catalogQuery.isSuccess,
  });

  const onFailure = (error: unknown) => {
    const wait = retryAfterSeconds(error);
    if (wait !== null) {
      setLimitedUntil(Date.now() + wait * 1000);
      return;
    }
    toast.error(errorMessage(error));
  };

  const searchMutation = useMutation({
    mutationFn: searchTikTokCreators,
    onSuccess: (data) => {
      setResult(data);
      setFromHistory(false);
      void queryClient.invalidateQueries({ queryKey: KEYS.history });
    },
    onError: onFailure,
  });

  const loadMoreMutation = useMutation({
    mutationFn: loadMoreTikTokCreators,
    onSuccess: setResult,
    onError: onFailure,
  });

  const openMutation = useMutation({
    mutationFn: getTikTokCreatorSearch,
    onSuccess: (data) => {
      setResult(data);
      setFilters(data.filters);
      setFromHistory(true);
      setHistoryOpen(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTikTokCreatorSearch,
    onSuccess: (_void, id) => {
      if (result?.id === id) setResult(null);
      void queryClient.invalidateQueries({ queryKey: KEYS.history });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const busy = searchMutation.isPending || loadMoreMutation.isPending || openMutation.isPending;
  const runSearch = () => searchMutation.mutate(compactFilters(filters));
  const blockedReason = secondsLeft ? `Try again in ${formatCountdown(secondsLeft)}` : null;

  const historyPanel = (className: string) => (
    <SearchHistory
      items={historyQuery.data ?? []}
      loading={historyQuery.isPending && catalogQuery.isSuccess}
      activeId={result?.id ?? null}
      onOpen={(id) => openMutation.mutate(id)}
      onDelete={(id) => deleteMutation.mutate(id)}
      onNew={() => {
        setResult(null);
        setFilters({});
        setFromHistory(false);
        setHistoryOpen(false);
      }}
      disabled={busy}
      className={className}
    />
  );

  return (
    <div className="flex h-[calc(100vh-1rem)] w-full md:h-[calc(100vh-4rem)]">
      {historyPanel("hidden md:flex w-64")}
      {historyOpen && (
        <>
          <button
            type="button"
            aria-label="Close search history"
            className="fixed inset-0 z-40 bg-black/30 md:hidden"
            onClick={() => setHistoryOpen(false)}
          />
          {historyPanel("fixed left-0 top-0 bottom-0 z-50 w-72 md:hidden shadow-xl")}
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="shrink-0 px-4 pb-4 pt-2 md:px-6">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">{header}</div>
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:hidden"
              aria-label="Open search history"
            >
              <History className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-8 md:px-6">
          {catalogQuery.isPending ? (
            <div className="flex h-48 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : block ? (
            <BlockedState block={block} onRetry={() => void catalogQuery.refetch()} />
          ) : catalogQuery.data ? (
            <div className="space-y-5">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  Creators on TikTok Shop open to brand collaborations · {catalogQuery.data.market} market · last 30 days
                </p>
              </div>

              <CreatorSearchFilters
                catalog={catalogQuery.data}
                filters={filters}
                onChange={setFilters}
                onSearch={runSearch}
                searching={searchMutation.isPending}
                blockedReason={blockedReason}
                disabled={openMutation.isPending}
              />

              {secondsLeft !== null && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  <Clock className="h-4 w-4 shrink-0" />
                  You have searched a lot in the last few minutes. Try again in{" "}
                  <span className="font-semibold tabular-nums">{formatCountdown(secondsLeft)}</span>. Past searches still open.
                </div>
              )}

              {result ? (
                <section className="space-y-3">
                  {fromHistory && (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                      <span>Results from {when(result.createdAt)}.</span>
                      <button
                        type="button"
                        onClick={runSearch}
                        disabled={busy || Boolean(blockedReason)}
                        className="inline-flex items-center gap-1 font-medium text-foreground hover:underline disabled:opacity-50"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Search again to refresh
                      </button>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold text-foreground">
                      {result.resultCount} {result.resultCount === 1 ? "creator" : "creators"} shown
                      <span className="ml-2 font-normal text-muted-foreground">{result.summary}</span>
                    </h2>
                    <SortSelect
                      catalog={catalogQuery.data}
                      value={filters.sortBy}
                      disabled={busy || Boolean(blockedReason)}
                      onChange={(sortBy) => {
                        const next = compactFilters({ ...filters, sortBy });
                        setFilters(next);
                        searchMutation.mutate(next);
                      }}
                    />
                  </div>
                  <CreatorResultsTable
                    creators={result.creators}
                    loading={searchMutation.isPending}
                    onOpen={setSelected}
                  />
                  {result.hasMore && (
                    <div className="flex justify-center pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => loadMoreMutation.mutate(result.id)}
                        disabled={busy}
                      >
                        {loadMoreMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                        Load more
                      </Button>
                    </div>
                  )}
                </section>
              ) : searchMutation.isPending ? (
                <CreatorResultsTable creators={[]} loading onOpen={setSelected} />
              ) : (
                <div className="flex flex-col items-center rounded-xl border border-dashed border-border px-6 py-12 text-center">
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100">
                    <Users className="h-6 w-6 text-brand-600" />
                  </div>
                  <h2 className="text-base font-semibold text-foreground">Find creators to work with</h2>
                  <p className="mt-1 max-w-md text-sm text-muted-foreground">
                    Set a niche, sales band or audience and press Search. Results include creators who have never sold
                    your products.
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {EXAMPLE_FILTERS.map((example) => (
                      <button
                        key={example.label}
                        type="button"
                        onClick={() => setFilters(compactFilters(example.build(catalogQuery.data)))}
                        className="rounded-full border border-border bg-card px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-brand-300 hover:text-brand-700"
                      >
                        {example.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <CreatorDetailSheet creator={selected} onOpenChange={(open) => !open && setSelected(null)} />
    </div>
  );
}

function BlockedState({ block, onRetry }: { block: CreatorSearchBlock; onRetry: () => void }) {
  const copy: Record<CreatorSearchBlock["kind"], { title: string; body: string }> = {
    not_connected: {
      title: "Connect your TikTok Shop to search creators",
      body: "Creator search runs on your shop’s TikTok Shop connection and covers creators in your shop’s market.",
    },
    reconnect: {
      title: "Reconnect your TikTok Shop",
      body: "TikTok stopped accepting the connection. Reconnect it from Integrations to search creators again.",
    },
    awaiting_approval: {
      title: "Creator search is waiting on TikTok",
      body: "It unlocks once TikTok approves the Creator Marketplace permission for Reveshare. Reconnect your shop after approval to turn it on.",
    },
    error: {
      title: "Creator search could not load",
      body: block.kind === "error" ? block.message : "",
    },
  };
  const { title, body } = copy[block.kind];
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center px-6 py-16 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
        <TikTokIcon className="h-7 w-7" />
      </div>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      <div className="mt-6">
        {block.kind === "error" ? (
          <Button type="button" variant="outline" onClick={onRetry}>
            Try again
          </Button>
        ) : (
          <Button asChild>
            <Link to="/brand/integrations">Go to Integrations</Link>
          </Button>
        )}
      </div>
    </div>
  );
}

function SearchHistory({
  items,
  loading,
  activeId,
  onOpen,
  onDelete,
  onNew,
  disabled,
  className,
}: {
  items: TikTokCreatorSearchHistoryItem[];
  loading: boolean;
  activeId: string | null;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  disabled: boolean;
  className?: string;
}) {
  return (
    <aside className={cn("flex-col border-r border-border bg-card", className)}>
      <div className="p-3">
        <Button type="button" variant="outline" className="w-full" onClick={onNew} disabled={disabled}>
          New creator search
        </Button>
      </div>
      <p className="px-4 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Past searches</p>
      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {loading ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">Your searches appear here and reopen without searching again.</p>
        ) : (
          <ul className="space-y-0.5">
            {items.map((item) => (
              <li key={item.id} className="group relative">
                <button
                  type="button"
                  onClick={() => onOpen(item.id)}
                  disabled={disabled}
                  className={cn(
                    "w-full rounded-lg px-2.5 py-2 pr-8 text-left transition-colors hover:bg-accent disabled:opacity-60",
                    activeId === item.id && "bg-accent",
                  )}
                >
                  <span className="line-clamp-2 text-sm text-foreground">{item.summary}</span>
                  <span className="text-xs text-muted-foreground">
                    {item.resultCount} creators · {when(item.createdAt)}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(item.id)}
                  disabled={disabled}
                  className="absolute right-1.5 top-2 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
                  aria-label={`Delete search ${item.summary}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
